import {GitHubAvatar} from '@github-ui/github-avatar'
import {ActionList, IconButton, NavList, Spinner, TextInput} from '@primer/react'
import styles from './styles.module.css'

import {useInfiniteQuery, useQuery, useQueryClient, type InfiniteData} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useState} from 'react'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {ChevronUpIcon, SearchIcon} from '@primer/octicons-react'
import {useDebounce} from '@github-ui/use-debounce'

export const DEFAULT_ITEMS_PER_PAGE = 5

export interface DynamicSidePanelItem {
  id: number
  value: string
  url: string
  avatar_url: string
  has_more?: boolean
}

interface SidePanelItemsListProps {
  items: DynamicSidePanelItem[] | DynamicSidePanelItem[][]
  onItemClick: () => void
}

function SidePanelItemsList({items, onItemClick}: SidePanelItemsListProps) {
  return (
    <>
      {items.flat().map(item => (
        <NavList.Item key={item.value} href={item.url} onClick={onItemClick}>
          <NavList.LeadingVisual>
            <GitHubAvatar size={16} src={item.avatar_url} />
          </NavList.LeadingVisual>
          {item.value}
        </NavList.Item>
      ))}
    </>
  )
}

interface UseSidePanelSearchProps {
  items: DynamicSidePanelItem[] | DynamicSidePanelItem[][]
  searchUrl?: string
  itemsPerPage?: number
}

function useSidePanelSearch({items, searchUrl, itemsPerPage}: UseSidePanelSearchProps) {
  const [searchMode, setSearchMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const DEBOUNCE_SPEED = 150
  const debouncedSetSearchTerm = useDebounce((value: string) => setDebouncedSearchTerm(value), DEBOUNCE_SPEED)

  const activateSearch = useCallback(() => {
    setSearchMode(true)
  }, [])

  const deactivateSearch = useCallback(() => {
    setSearchMode(false)
    setSearchTerm('')
    setDebouncedSearchTerm('')
  }, [])

  const handleSearchTermChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchTerm(value) // Update immediately for UI responsiveness
      debouncedSetSearchTerm(value) // Update debounced term for querying
    },
    [debouncedSetSearchTerm],
  )

  // Use search query hook when in search mode and search URL is available
  const searchQuery = useSidePanelItemsSearch({
    searchUrl: searchUrl || '',
    searchTerm: debouncedSearchTerm,
    itemsPerPage,
  })

  // Determine which items to render: search results or original items
  const renderedItems = searchMode && debouncedSearchTerm && searchQuery.data ? searchQuery.data : items
  return {
    searchMode,
    searchTerm,
    searchQuery,
    activateSearch,
    deactivateSearch,
    handleSearchTermChange,
    renderedItems,
  }
}

export interface DynamicSidePanelProps {
  /**
   * The items to show in the dynamic list.
   * This supports a paginated groups of items for easy integration with useInfiniteQuery.
   */
  items: DynamicSidePanelItem[] | DynamicSidePanelItem[][]

  /** Title for the full section */
  title: string

  /** Label for the search input field */
  searchLabel?: string

  /** URL for search endpoint (when provided, enables search functionality) */
  searchUrl?: string

  /** If a `Load Next Page` button should be shown */
  hasNextPage?: boolean

  /** If the data is currently being loaded in order to display a spinner */
  isLoading?: boolean

  /** Callback for loading another page when the user requests more items */
  fetchNextPage?: () => void

  /** Action to send for analytics actions */
  analytics: {
    /** Optional sub-category to send with analytics events */
    subCategory?: string

    /** Action to send for users interacting with the "Show More" action */
    showMoreAction: string

    /** Action to send for users interacting w/ an individual item */
    itemAction: string
  }
  /** Number of items to fetch per page */
  itemsPerPage?: number
}

export function DynamicSidePanelItems(props: DynamicSidePanelProps) {
  const {items, title, hasNextPage, itemsPerPage, isLoading, fetchNextPage, analytics, searchLabel, searchUrl} = props
  const {sendItemClick} = useGlobalNavAnalytics(props.analytics.subCategory)
  const [showMoreClickCount, setShowMoreClickCount] = useState(0)

  const {searchMode, searchTerm, searchQuery, activateSearch, deactivateSearch, handleSearchTermChange, renderedItems} =
    useSidePanelSearch({
      items,
      searchUrl,
      itemsPerPage,
    })

  return (
    <>
      <NavList.Group>
        <NavList.GroupHeading as="h3" className={styles.groupHeading}>
          <div>{title}</div>
          {searchUrl && searchLabel && !searchMode ? (
            <IconButton
              size="small"
              variant="invisible"
              aria-label={searchLabel}
              icon={SearchIcon}
              data-testid="dynamic-side-panel-items-search-button"
              onClick={activateSearch}
            />
          ) : searchMode ? (
            <IconButton
              size="small"
              variant="invisible"
              aria-label="Close search"
              icon={ChevronUpIcon}
              onClick={deactivateSearch}
            />
          ) : null}
        </NavList.GroupHeading>

        {searchMode && (
          <>
            <NavList.Item>
              <div className="d-flex width-full position-relative">
                <TextInput
                  key="search-input"
                  autoFocus
                  aria-label={searchLabel}
                  placeholder={searchLabel}
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  leadingVisual={SearchIcon}
                  className="mr-1 flex-auto"
                />

                {searchQuery.isLoading && (
                  <div className={styles.searchSpinner}>
                    <Spinner size="small" />
                  </div>
                )}
              </div>
            </NavList.Item>
          </>
        )}

        <SidePanelItemsList items={renderedItems} onItemClick={() => sendItemClick(analytics.itemAction)} />

        {!isLoading && !searchMode && hasNextPage && fetchNextPage && (
          <ActionList.Item
            onSelect={() => {
              sendItemClick(analytics.showMoreAction)
              setShowMoreClickCount(prevCount => prevCount + 1)
              fetchNextPage()
            }}
            data-testid="dynamic-side-panel-items-show-more"
          >
            <span className="color-fg-muted f6">{showMoreClickCount >= 2 ? 'Show even more' : 'Show more'}</span>
          </ActionList.Item>
        )}
      </NavList.Group>
      {isLoading && (
        <NavList.Group className="d-flex flex-justify-center width-full my-3">
          <Spinner data-testid="dynamic-side-panel-items-loader" size="small" />
        </NavList.Group>
      )}
    </>
  )
}

export interface DynamicMenuItemsProps {
  searchUrl?: string
  initialItems?: DynamicSidePanelItem[]
  searchTerm?: string
  itemsPerPage?: number
}

export function useSidePanelItemsQuery(props: DynamicMenuItemsProps) {
  const {searchUrl, itemsPerPage = DEFAULT_ITEMS_PER_PAGE, initialItems} = props

  const queryClient = useQueryClient()

  const query = useInfiniteQuery<
    DynamicSidePanelItem[],
    Error,
    InfiniteData<DynamicSidePanelItem[]>,
    unknown[],
    number
  >({
    queryKey: ['global-nav-side-panel-items', searchUrl, itemsPerPage],
    initialPageParam: initialItems ? 2 : 1,
    initialData: {
      pages: initialItems ? [initialItems] : [],
      pageParams: initialItems ? [1] : [],
    },
    staleTime: 1000,
    refetchOnMount: false,
    queryFn: async ({pageParam}) => {
      const response = await reactFetchJSON(`${searchUrl}?page=${pageParam}&items_per_page=${itemsPerPage}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }
      const sidePanelItems = await (response.json() as Promise<DynamicSidePanelItem[]>)
      return sidePanelItems
    },
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      if (typeof lastPage[0]?.has_more !== 'undefined') {
        return lastPage[0]?.has_more ? lastPageParam + 1 : null
      } else if (lastPage.length < itemsPerPage) {
        return null
      }
      return lastPageParam + 1
    },
  })

  // TODO: Hack for now, want to reset the current page number when the dialog menu closes
  useEffect(() => {
    return () => {
      if (query.isFetched) {
        queryClient.resetQueries({queryKey: ['global-nav-side-panel-items', searchUrl]})
      }
    }
  }, [queryClient, query.isFetched, searchUrl])

  return query
}

export function useSidePanelItemsSearch(props: DynamicMenuItemsProps) {
  const {searchUrl, searchTerm, itemsPerPage = DEFAULT_ITEMS_PER_PAGE} = props
  const query = useQuery({
    queryKey: ['global-nav-side-panel-items-search', searchUrl, searchTerm, itemsPerPage],
    queryFn: async () => {
      if (!searchTerm) return []
      const response = await reactFetchJSON(
        `${searchUrl}?q=${encodeURIComponent(searchTerm)}&items_per_page=${itemsPerPage}`,
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }
      const sidePanelItems = await (response.json() as Promise<DynamicSidePanelItem[]>)
      return sidePanelItems
    },
    enabled: Boolean(searchTerm?.trim()) && Boolean(searchUrl?.trim()),
  })

  return query
}

try{ SidePanelItemsList.displayName ||= 'SidePanelItemsList' } catch {}
try{ DynamicSidePanelItems.displayName ||= 'DynamicSidePanelItems' } catch {}